import { useEffect, useState } from 'react'

import { useVariantLensApiClient } from '../auth/hooks/useVariantLensApiClient'
import type { VariantTrack, VariantTracksResponse } from './types'

export type UseCaseVariantTracksResult = {
  tracks: VariantTrack[]
  /** track_id queried when ?track= is omitted, or null until tracks load. */
  defaultTrackId: string | null
  loading: boolean
}

/**
 * Fetch the selectable variant VCFs for a case from
 * GET /cases/{caseId}/variant-tracks (GEN-3597).
 *
 * Returns an empty list (and null default) while loading or on error/404, so
 * the caller simply hides the selector and falls back to the API's implicit
 * default track. The track flagged `default` (else the first listed) is
 * surfaced as `defaultTrackId`.
 *
 * `basePath` (GEN-5495) overrides the resource prefix so the same hook backs
 * the order and benchmark viewers, whose variant tracks live under
 * `/orders/{token}` and `/benchmarks/{set}/{sample}` but share the
 * `/variant-tracks` suffix and response shape. It must be a caller-built,
 * already-encoded path (no trailing slash); when omitted the case path is used
 * so existing callers are unchanged.
 */
export function useCaseVariantTracks(caseId: string | undefined, basePath?: string): UseCaseVariantTracksResult {
  const apiClient = useVariantLensApiClient()
  const [tracks, setTracks] = useState<VariantTrack[]>([])
  // Start "loading" so a deep link with ?track= can wait for the real track
  // list before the variants fetch fires, instead of briefly querying the
  // default VCF and refetching once tracks resolve.
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!caseId) {
      setTracks([])
      setLoading(false)
      return
    }
    const abort = new AbortController()
    setLoading(true)
    setTracks([])
    const base = basePath ?? `/cases/${encodeURIComponent(caseId)}`
    const url = `${base}/variant-tracks`
    void (async () => {
      try {
        const res = await apiClient.get(url, undefined, true, abort.signal)
        if (!res.ok) return
        const body = (await res.json()) as VariantTracksResponse
        if (abort.signal.aborted) return
        setTracks(body.tracks ?? [])
      } catch {
        // Network/abort: stay empty; the caller hides the selector.
      } finally {
        if (!abort.signal.aborted) setLoading(false)
      }
    })()
    return () => abort.abort()
  }, [apiClient, caseId, basePath])

  const defaultTrackId = tracks.find((t) => t.default)?.track_id ?? tracks[0]?.track_id ?? null
  return { tracks, defaultTrackId, loading }
}
